import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from '@phosphor-icons/react';

// Turn a Photon GeoJSON feature into a readable one-line address + a split label.
function formatFeature(f) {
  const p = f.properties || {};
  const line1 = p.housenumber && p.street
    ? `${p.housenumber} ${p.street}`
    : (p.name || p.street || '');
  const rest = [p.city || p.district || p.county, p.state, p.postcode, p.country].filter(Boolean);
  const full = [line1, ...rest].filter(Boolean).join(', ');
  return { full, primary: line1 || full, secondary: rest.join(', ') };
}

/**
 * Address field with live place lookup. Type ≥3 chars and pick a real address
 * from the dropdown. Uses Photon (photon.komoot.io) — a free, keyless, CORS-enabled
 * OpenStreetMap geocoder. Swap for Google Places later by changing the fetch below.
 */
export default function AddressAutocomplete({ value, onChange, placeholder, className, testId }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  const skipNextFetch = useRef(false); // don't re-search right after a pick

  // keep in sync if the parent resets the value
  useEffect(() => { setQuery(value || ''); }, [value]);

  // debounced lookup
  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    const q = query.trim();
    if (q.length < 3) { setResults([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=5`, { signal: ctrl.signal });
        const d = await r.json();
        const seen = new Set();
        const opts = (d.features || [])
          .map(formatFeature)
          .filter(o => o.full && !seen.has(o.full) && seen.add(o.full));
        setResults(opts);
        setOpen(opts.length > 0);
        setHighlight(-1);
      } catch (e) {
        if (e.name !== 'AbortError') { setResults([]); setOpen(false); }
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);

  // close when clicking outside
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (opt) => {
    skipNextFetch.current = true;
    setQuery(opt.full);
    onChange(opt.full);
    setResults([]);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <MapPin size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); }}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className={`${className || ''} pl-10`}
          data-testid={testId}
        />
        {loading && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          className="absolute z-30 mt-1 w-full bg-card border border-border rounded-xl shadow-lift overflow-hidden max-h-64 overflow-y-auto"
          role="listbox"
          data-testid={testId ? `${testId}-results` : 'address-results'}
        >
          {results.map((opt, i) => (
            <li key={`${opt.full}-${i}`} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3.5 py-2.5 flex items-start gap-2.5 cursor-pointer transition-colors ${i === highlight ? 'bg-secondary' : 'hover:bg-secondary/60'}`}
                data-testid={testId ? `${testId}-option-${i}` : `address-option-${i}`}
              >
                <MapPin size={15} className="text-primary shrink-0 mt-0.5" />
                <span className="min-w-0">
                  <span className="block text-sm text-foreground truncate">{opt.primary}</span>
                  {opt.secondary && <span className="block text-[11px] text-muted-foreground truncate">{opt.secondary}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
